import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle, ChevronDown, Info, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { fetchVatMetadata, allocationOptions } from '../../../services/api/vatMetadataApi';
import { VatDecisionControls, VatTreatmentExplanation, useVatFieldRegister } from '../Reporting/Vat/UnmappedAccountsPanel';
import SettingsCard from './SettingsCard';
import SettingsSelect from './SettingsSelect';
import VatAskAILink from '../Reporting/Vat/VatAskAILink';

/**
 * Kontoplanshälsa — the self-service and support surface for chart problems.
 *
 * The harmonisation spec makes this the ONLY tool support uses: everything it
 * can do here, a user can do too, and nothing here edits history. Findings are
 * observations plus, where relevant, an action that is always explicit.
 *
 * UX_PLAN.md P3: the page has two halves. "Behöver åtgärd" holds what needs a
 * decision — each item says what is wrong, what it affects and what to do, and
 * carries the control that does it. "Information" holds everything else,
 * folded, with one line per kind saying why there is nothing to do. A user who
 * arrives from the import result finds the same VAT decision here that the
 * result card offered, recorded through the same endpoint family (attributed
 * `chart_health`).
 *
 * The one category that must not be softened is required-resolution VAT review.
 * A BAS2026 discount balance on 3730–3732 deliberately has no static VAT field,
 * because a discount has to reduce the ORIGINATING sale's field — allocating it
 * is a question only a person can answer. It is mandatory to answer and clearly
 * visible, but it does not hard-block a filing.
 */

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };

/**
 * The ALLOCATION key: what a split is stored and saved under.
 *
 * The render and the save once disagreed about this, so an acquisition split
 * was written under one key and read back under another and the save silently
 * did nothing. There is deliberately one definition.
 */
const findingKey = (finding) => `${finding.kind}:${finding.subjectKey ?? ''}`;

/**
 * The RENDER key: React identity, which is a different question.
 *
 * Most findings have no subjectKey, so keying the list on the allocation key
 * would give every provenance or synonym finding the same key — and a split
 * produces several supersession findings that would collide too. The index
 * disambiguates the list without touching the allocation key.
 */
const renderKey = (finding, index) => [
    finding.kind,
    finding.subjectKey ?? '',
    finding.accountNumber ?? finding.accountNumbers?.join('-') ?? finding.verificationId ?? '',
    index,
].join(':');

const KIND_LABELS = {
    discount_requires_allocation: 'Rabatt måste fördelas till rätt momsfält',
    vat_relevant_without_box: 'Behöver ditt svar om momsbehandling',
    vat_mapping_conflict: 'Momsinställning behöver kontrolleras',
    vat_automatic_treatment: 'Automatisk bedömning och separat redovisning',
    vat_treatment_unavailable: 'Momskontrollen kunde inte köras',
    vat_field_contradiction: 'Momsrapportkod stämmer inte med kontofamiljen',
    vat_family_unmapped: 'Momsfamilj utan momsrapportkod',
    legacy_3108_missing_field_35: 'EU-varuförsäljning saknar fält 35',
    provenance_violation: 'Kontots ursprungsmärkning stämmer inte',
    name_differs_from_catalog: 'Namnet i importfilen skiljer sig från kontoplanen',
    synonym_in_use: 'Två konton med samma betydelse används',
    legacy_candidate: 'Konto utanför BAS-kontoplanen som används',
    archive_candidate: 'Oanvänt konto utanför BAS-kontoplanen',
    k2_not_allowed_with_balance: 'Kontot får inte användas av K2-företag',
    eu_acquisition_requires_allocation: 'EU-/importförvärv: varor eller tjänster?',
    allocation_superseded: 'Sparad fördelning används inte längre',
    metadata_review_required: 'Moms-/SRU-koder hör till en betydelse som valdes bort',
    import_evidence: 'Skapat eller namngivet vid import',
};

// One line per informational kind: why there is nothing to do.
const KIND_DESCRIPTIONS = {
    vat_automatic_treatment: 'Ingen direkt momsrapportkod behövs för dessa bokningar. Läs varför, och se eventuell separat redovisning eller momstidpunkt.',
    name_differs_from_catalog: 'Ditt kontonamn behålls; filens namn är sparat som information.',
    import_evidence: 'Vad importen skapade eller namngav — underlag, inget att göra.',
    synonym_in_use: 'Båda kontona fungerar. Att slå ihop dem är frivillig städning.',
    legacy_candidate: 'Kontot behålls med sin historik.',
    archive_candidate: 'Kan arkiveras för att korta kontovalet — historik och rapporter påverkas inte.',
    allocation_superseded: 'En tidigare fördelning har ersatts av en ny kod eller ett nytt beslut.',
    provenance_violation: 'Kontots märkning som standardkonto eller eget konto stämmer inte med kontoplanen.',
    k2_not_allowed_with_balance: 'Rådgivande: kontot hör inte hemma i ett K2-bokslut.',
};

const fmtKr = (value) => `${new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(Number(value) || 0)} kr`;
const chartLabel = (code) => (code ? String(code).replace(/^BAS(\d{4})$/, 'BAS $1') : '');

/** Does this group belong under "Behöver åtgärd"? */
export function groupNeedsAction(group) {
    return group.needsAction > 0 || group.tone === 'error'
        || (['vat_relevant_without_box', 'vat_field_contradiction', 'vat_family_unmapped',
            'legacy_3108_missing_field_35', 'metadata_review_required'].includes(group.kind)
            && (group.items || []).some(({ finding }) => !finding.resolved));
}

/**
 * "Vad / Påverkar / Gör så här" for the kinds whose findings carry the facts
 * (UX_PLAN.md P3). Anything else falls back to the backend sentence.
 */
export function describeActionFinding(finding) {
    const number = finding.accountNumber;
    const name = finding.accountName ? ` ${finding.accountName}` : '';
    switch (finding.kind) {
        case 'vat_relevant_without_box':
            return {
                // The card's head names the account; the facts do not repeat it.
                what: 'Bekräfta momsbehandlingen för kontot.',
                effect: `${fmtKr(finding.balance)} i nettorörelse${finding.detail?.period
                    ? ` (${finding.detail.period.start_date} – ${finding.detail.period.end_date})` : ''}. Det är inte i sig ett momsunderlag eller skatt att betala.`,
                how: finding.proposal?.vatBox ? 'Kontrollera förslaget och bekräfta om det stämmer med bokningarna.'
                    : 'Kontrollera vad bokningarna avser. Därefter kan du avgöra om ett momsrapportsfält behövs.',
            };
        case 'vat_mapping_conflict':
            return { what: `Momsinställningen för konto ${number}${name} stämmer inte med den identifierade bokningen.`,
                effect: `Nettorörelse ${fmtKr(finding.balance)}. Nuvarande koppling kan ge fel belopp i ett utkast; låsta deklarationer ändras inte.`,
                how: 'Läs underlaget nedan och rätta kopplingen om den identifierade betydelsen stämmer.' };
        case 'discount_requires_allocation':
            return {
                what: `Konto ${number}${name} har en rabatt som saknar fördelning.`,
                effect: finding.balance !== undefined
                    ? `${fmtKr(finding.balance)} minskar inte rätt försäljningsfält i momsdeklarationen förrän du fördelat beloppet.`
                    : 'Rabatten minskar inte rätt försäljningsfält förrän du fördelat beloppet.',
                how: 'Fördela beloppet på det eller de fält som den ursprungliga försäljningen redovisades i.',
            };
        case 'eu_acquisition_requires_allocation':
            return {
                what: 'Ett EU- eller importförvärv saknar uppgift om det gäller varor eller tjänster.',
                effect: 'Underlaget hamnar i fel fält i momsdeklarationen förrän det är fördelat.',
                how: 'Fördela beloppet på varor och/eller tjänster nedan.',
            };
        case 'metadata_review_required':
            return {
                what: `Konto ${number}${name} fick filens betydelse vid import, men bär koder från kontoplanens betydelse.`,
                effect: 'Koderna kan skicka beloppen till fel fält eller fel deklarationsrad.',
                how: 'Kontrollera koderna i Kontoplanen och markera dem som granskade här när de stämmer.',
            };
        default:
            return null;
    }
}

const ChartHealthSection = ({ companyId, fiscalYearId = null, fiscalYears = [], onSelectFiscalYear = null }) => {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [healthContext, setHealthContext] = useState(null);
    const requestSequence = useRef(0);
    const [allocations, setAllocations] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    // Explicit open/closed per finding kind. Absent = follow the default
    // (action groups open, information folded).
    const [openKinds, setOpenKinds] = useState({});
    // VAT is filed per period, so a review amount and its allocation are only
    // meaningful inside one. The periods come from the tenant's own
    // vat_reporting_periods — the server rejects anything else.
    const [periods, setPeriods] = useState([]);
    const [periodId, setPeriodId] = useState('');
    const [periodError, setPeriodError] = useState(null);
    const [vatFieldOptions, setVatFieldOptions] = useState([]);
    const { fieldOptions, shortLabels } = useVatFieldRegister(companyId);

    useEffect(() => {
        let cancelled = false;
        fetchVatMetadata(companyId)
            .then((metadata) => { if (!cancelled) setVatFieldOptions(allocationOptions(metadata)); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    const period = periods.find((p) => p.vat_reporting_period_id === periodId) || null;
    const fiscalYear = (fiscalYears || []).find((fy) => String(fy.fiscal_year_id) === String(fiscalYearId)) || null;
    const contextKey = `${companyId}:${fiscalYearId || ''}:${period?.start_date || ''}:${period?.end_date || ''}`;
    // The range a VAT decision is re-checked over: the chosen period, else the
    // fiscal year itself.
    const decisionRange = period
        ? { start_date: period.start_date, end_date: period.end_date }
        : (fiscalYear ? { start_date: String(fiscalYear.start_date).slice(0, 10), end_date: String(fiscalYear.end_date).slice(0, 10) } : null);

    useEffect(() => {
        if (!fiscalYearId) { setPeriods([]); setPeriodId(''); setPeriodError(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const url = getApiUrl(
                    `${companyId}/reporting/vat-reporting-periods?fiscalYearId=${fiscalYearId}`
                );
                const { data } = await axiosInstance.get(url);
                if (cancelled) return;
                const list = data?.periods || data?.data || [];
                setPeriods(list);
                // Reset rather than keep the previous fiscal year's period id,
                // which does not exist in the new list — that left `period`
                // undefined and quietly ran the periodless health check.
                setPeriodId(list[0]?.vat_reporting_period_id || '');
                setPeriodError(list.length === 0 ? 'Inga momsperioder finns för det här räkenskapsåret.' : null);
            } catch (error) {
                if (cancelled) return;
                setPeriods([]);
                setPeriodId('');
                // Swallowing this showed "Inga anmärkningar" while every
                // period-based VAT finding was simply never computed.
                setPeriodError(
                    error.response?.data?.message || 'Momsperioderna kunde inte hämtas.'
                );
            }
        })();
        return () => { cancelled = true; };
    }, [companyId, fiscalYearId]);

    const load = useCallback(async () => {
        const request = ++requestSequence.current;
        setLoading(true);
        setLoadError(null);
        try {
            // The chart version is resolved server-side from recorded
            // provenance — the client does not get to choose which rules apply
            // to a filing.
            const params = new URLSearchParams();
            if (fiscalYearId) params.set('fiscalYearId', fiscalYearId);
            if (period?.start_date) {
                params.set('periodStart', period.start_date);
                params.set('periodEnd', period.end_date);
            }
            const query = params.toString() ? `?${params.toString()}` : '';
            const url = getApiUrl(`${companyId}/accounting/chart/health${query}`);
            const { data } = await axiosInstance.get(url);
            if (request !== requestSequence.current) return;
            if (data.success) {
                setHealth(data.data);
                setHealthContext(contextKey);
            } else {
                setLoadError(data.message || 'Kontoplanshälsan kunde inte hämtas');
            }
        } catch (error) {
            if (request === requestSequence.current) {
                setLoadError(error.response?.data?.message || 'Kontoplanshälsan kunde inte hämtas');
            }
        } finally {
            if (request === requestSequence.current) setLoading(false);
        }
    }, [companyId, fiscalYearId, period?.start_date, period?.end_date, contextKey]);

    useEffect(() => {
        load();
        return () => { requestSequence.current += 1; };
    }, [load]);

    /**
     * Save the split for one review item.
     *
     * A balance can hold discounts from domestic, export and EU sales at once,
     * so the answer is several shares, not one box. The whole split is sent
     * together and replaces whatever was recorded for the period.
     */
    const saveAllocation = async (finding) => {
        const key = findingKey(finding);
        const rows = (allocations[key] || []).filter((row) => row.field && row.amount !== '');
        if (rows.length === 0) return;
        if (!period) {
            toast.error('Välj en momsperiod innan du fördelar — moms redovisas per period.');
            return;
        }

        setSavingKey(key);
        try {
            const url = getApiUrl(`${companyId}/accounting/chart/vat-review/allocations`);
            const { data } = await axiosInstance.post(url, {
                fiscalYearId,
                periodStart: period.start_date,
                periodEnd: period.end_date,
                itemKind: finding.kind,
                subjectKey: finding.subjectKey,
                allocations: rows.map((row) => ({ field: row.field, amount: Number(row.amount) })),
            });
            if (data.success) {
                toast.success(`Fördelningen sparad på ${rows.length} fält`);
                await load();
            } else {
                toast.error(data.message || 'Fördelningen kunde inte sparas');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Fördelningen kunde inte sparas');
        } finally {
            setSavingKey(null);
        }
    };

    const setRow = (key, index, patch) => {
        setAllocations((prev) => {
            const rows = [...(prev[key] || [{ field: '', amount: '' }])];
            rows[index] = { ...rows[index], ...patch };
            return { ...prev, [key]: rows };
        });
    };

    const addRow = (key) => {
        setAllocations((prev) => ({
            ...prev,
            [key]: [...(prev[key] || [{ field: '', amount: '' }]), { field: '', amount: '' }],
        }));
    };

    const removeRow = (key, index) => {
        setAllocations((prev) => ({
            ...prev,
            [key]: (prev[key] || []).filter((_, i) => i !== index),
        }));
    };

    /**
     * Close a metadata-review item.
     *
     * This finding has no allocatable fields, so it needs its own action —
     * otherwise the warning and the required-resolution count would stand
     * forever with nothing a user could do about them.
     */
    const confirmMetadata = async (finding) => {
        const key = `${finding.kind}:${finding.accountNumber}`;
        setSavingKey(key);
        try {
            const url = getApiUrl(
                `${companyId}/accounting/chart/accounts/${finding.accountNumber}/metadata-review`
            );
            const { data } = await axiosInstance.post(url, { confirmExisting: true });
            if (data.success) {
                toast.success(`Koderna för konto ${finding.accountNumber} är granskade`);
                await load();
            } else {
                toast.error(data.message || 'Granskningen kunde inte sparas');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Granskningen kunde inte sparas');
        } finally {
            setSavingKey(null);
        }
    };

    /**
     * Decide an account without a momsrapportkod, here (UX_PLAN.md P3).
     *
     * The same decision the momsdeklaration wizard and the import result card
     * record — `VatUnmappedAccountService.resolveAccount` through the chart
     * health route, attributed `chart_health` in the account's history. The
     * server re-runs the detection over the range; the page reloads its own
     * findings from the server rather than removing the row itself.
     */
    const decideVatGap = async (finding, body, successText) => {
        if (!finding.accountId) {
            toast.error('Kontot kan inte kopplas härifrån — öppna det i Kontoplanen.');
            return;
        }
        if (!decisionRange) {
            toast.error('Välj ett räkenskapsår först.');
            return;
        }
        const key = `${finding.kind}:${finding.accountNumber}`;
        setSavingKey(key);
        try {
            const url = getApiUrl(`${companyId}/accounting/chart/health/unmapped-accounts/resolve`);
            await axiosInstance.post(url, { account_id: finding.accountId, ...decisionRange, ...body });
            toast.success(successText);
            await load();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kontot kunde inte uppdateras.');
        } finally {
            setSavingKey(null);
        }
    };

    const findings = useMemo(() => [...(health?.findings || [])].sort((a, b) => {
        const required = Number(Boolean(b.requiredResolution && !b.resolved))
            - Number(Boolean(a.requiredResolution && !a.resolved));
        if (required !== 0) return required;
        return (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3);
    }), [health]);

    // Findings grouped by kind. Flat, this list was 16 599px — twenty-two
    // screens — because it repeats the same sentence once per account.
    // Grouped, the same information is a dozen rows you can read.
    const groups = useMemo(() => {
        const list = [];
        findings.forEach((finding, index) => {
            let group = list.find((g) => g.kind === finding.kind);
            if (!group) {
                group = { kind: finding.kind, items: [], needsAction: 0, tone: 'warning' };
                list.push(group);
            }
            group.items.push({ finding, index });
            if (finding.requiredResolution && !finding.resolved) group.needsAction += 1;
            if (finding.severity === 'error') group.tone = 'error';
        });
        return list;
    }, [findings]);

    const actionGroups = groups.filter(groupNeedsAction);
    const infoGroups = groups.filter((g) => !groupNeedsAction(g));
    const actionCount = actionGroups.reduce((n, g) => n + g.items.length, 0);
    const infoCount = infoGroups.reduce((n, g) => n + g.items.length, 0);

    if (loadError) {
        return (
            <SettingsCard icon={Activity} title="Kontoplanshälsa">
                <div role="alert">{loadError} Inga aktuella kontrollresultat kan visas.</div>
                <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={load} disabled={loading}>
                    Försök igen
                </button>
            </SettingsCard>
        );
    }
    if (loading || !health || healthContext !== contextKey) {
        return <SettingsCard icon={Activity} title="Kontoplanshälsa">Hämtar kontoplanshälsa…</SettingsCard>;
    }

    // Action groups open, information folded; `openKinds` holds the user's
    // explicit toggles, so clicking a group always wins.
    const isGroupOpen = (group) => {
        if (Object.prototype.hasOwnProperty.call(openKinds, group.kind)) return openKinds[group.kind];
        return groupNeedsAction(group);
    };
    const toggleGroup = (group) => {
        setOpenKinds((prev) => ({ ...prev, [group.kind]: !isGroupOpen(group) }));
    };

    const renderVatDecision = (finding) => {
        const key = `${finding.kind}:${finding.accountNumber}`;
        const account = {
            account_id: finding.accountId,
            account_number: finding.accountNumber,
            treatment: finding.detail?.treatment,
            managed_vat: finding.detail?.managedVat,
            question: finding.detail?.question,
            current_vat_box: finding.detail?.currentVatBox,
            period: finding.detail?.period,
            proposal: finding.proposal?.vatBox
                ? { vat_box: String(finding.proposal.vatBox), explanation: finding.proposal.explanation }
                : null,
        };
        // The controls name the proposal themselves; the card's head names the
        // account and the amount, so nothing is repeated here.
        return (
            <div style={{ marginTop: '0.25rem' }}>
                <VatDecisionControls
                    account={account}
                    reviewContext={{ fiscalYearId, companyId }}
                    busy={savingKey === key}
                    fieldOptions={fieldOptions}
                    shortLabels={shortLabels}
                    onAccept={(box) => decideVatGap(finding, { vat_box: box }, `Konto ${finding.accountNumber} kopplat till fält ${box}.`)}
                    onChoose={(box) => decideVatGap(finding, { vat_box: box }, `Konto ${finding.accountNumber} kopplat till fält ${box}.`)}
                    onExclude={() => decideVatGap(finding, { exclude: true }, `Konto ${finding.accountNumber} ingår inte i momsdeklarationen.`)}
                    onClear={() => decideVatGap(finding, { clear_mapping: true }, `Kopplingen borttagen. Bokföringen kontrolleras igen.`)}
                />
            </div>
        );
    };

    const renderFinding = (finding, index, { action }) => {
        // Allocation state is keyed by subject; React identity is
        // keyed by position. Conflating them caused both a silent
        // save failure and duplicate list keys.
        const key = findingKey(finding);
        // The editor stays available after an item is resolved: an
        // allocation with the right total but the wrong box is
        // still wrong, and a filing decision has to remain
        // changeable.
        const isAllocatable = (finding.allowedFields || []).length > 0;
        const needsAllocation = finding.requiredResolution && !finding.resolved;
        const described = action ? describeActionFinding(finding) : null;
        // A decision card: the account as its head, the amount beside it, the
        // facts below. It is a question, not an error, so it is a plain white
        // card rather than a red fill (the header already says it does not
        // stop the bookkeeping).
        const isDecision = Boolean(described) && Boolean(finding.accountNumber);
        // The VAT decision card says everything through its head and its
        // controls; the what/effect/how list stays for the other action kinds.
        const showFacts = Boolean(described) && finding.kind !== 'vat_relevant_without_box';
        const decisionPeriod = finding.detail?.period
            ? `${finding.detail.period.start_date} – ${finding.detail.period.end_date}`
            : (fiscalYear ? `${String(fiscalYear.start_date).slice(0, 10)} – ${String(fiscalYear.end_date).slice(0, 10)}` : null);
        return (
            <div
                key={renderKey(finding, index)}
                className={isDecision
                    ? 'health-action'
                    : `warning-item ${finding.severity === 'error' || needsAllocation ? 'warning-error' : ''}`}
                style={isDecision ? undefined : { flexDirection: 'column', alignItems: 'stretch' }}
            >
                {isDecision && (
                    <div className="health-action__head">
                        <span className="health-action__account">
                            <span className="health-action__number">{finding.accountNumber}</span>
                            {finding.accountName && <span className="health-action__name">{finding.accountName}</span>}
                        </span>
                        {finding.balance !== undefined && finding.balance !== null && (
                            <span className="health-action__amount" title={`Nettorörelse${decisionPeriod ? ` ${decisionPeriod}` : ''} — inte i sig ett momsunderlag eller skatt att betala`}>
                                {fmtKr(finding.balance)}
                                {decisionPeriod && <span className="health-action__period"> · {decisionPeriod}</span>}
                            </span>
                        )}
                    </div>
                )}
                <div className="warning-content">
                    {showFacts ? (
                        <dl className="health-finding__facts">
                            <dt>Vad</dt><dd>{described.what}</dd>
                            <dt>Påverkar</dt><dd>{described.effect}</dd>
                            <dt>Gör så här</dt><dd>{described.how}</dd>
                        </dl>
                    ) : finding.kind === 'vat_automatic_treatment' ? (
                        <>
                            <p className="warning-message">{finding.accountNumber} {finding.accountName}</p>
                            <VatTreatmentExplanation treatment={finding.detail?.treatment} period={finding.detail?.period} />
                            <VatAskAILink accountId={finding.accountId} accountNumber={finding.accountNumber} fiscalYearId={fiscalYearId} />
                        </>
                    ) : described ? null : (
                        <p className="warning-message">{finding.message}</p>
                    )}
                    {(finding.allocations || []).length > 0 && (
                        <p
                            className="warning-message"
                            style={{ color: finding.resolved ? 'var(--primary)' : undefined }}
                        >
                            {finding.resolved ? <CheckCircle size={13} /> : <Info size={13} />}
                            {' '}Fördelat: {finding.allocations
                                .map((a) => `fält ${a.field} ${a.amount}`)
                                .join(', ')}
                            {!finding.resolved && ' — summan täcker inte hela beloppet.'}
                        </p>
                    )}
                    {/* I7: an alias- or catalogue-derived VAT box is a
                        proposal, never something applied on its own. */}
                    {finding.proposal?.vatBox && finding.kind !== 'vat_relevant_without_box' && (
                        <p className="warning-message">
                            <Info size={13} /> Förslag: momsrapportkod {finding.proposal.vatBox}.
                            Måste bekräftas innan den används.
                        </p>
                    )}
                </div>

                {['vat_relevant_without_box', 'vat_mapping_conflict'].includes(finding.kind) && !finding.resolved && renderVatDecision(finding)}

                {needsAllocation && finding.kind === 'metadata_review_required' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={savingKey === `${finding.kind}:${finding.accountNumber}`}
                            onClick={() => confirmMetadata(finding)}
                        >
                            {savingKey === `${finding.kind}:${finding.accountNumber}` ? 'Sparar…' : 'Koderna stämmer — markera som granskat'}
                        </button>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--secondary, #717579)' }}>
                            Behöver de ändras gör du det i kontoplanen.
                        </span>
                    </div>
                )}

                {isAllocatable && (
                    <div style={{ marginTop: '0.5rem' }}>
                        {/* A balance can hold discounts from domestic,
                            export and EU sales at once, so the answer is
                            a split rather than one box. */}
                        {(allocations[key]
                            || (finding.allocations || []).map((a) => ({
                                field: a.field, amount: String(a.amount),
                            }))
                            || []
                        ).concat(
                            (allocations[key] || finding.allocations || []).length
                                ? [] : [{ field: '', amount: '' }]
                        ).map((row, rowIndex) => (
                            <div
                                key={rowIndex}
                                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.375rem', flexWrap: 'wrap' }}
                            >
                                <div style={{ flex: '1 1 260px', maxWidth: 420 }}>
                                    {/* Only the boxes this item can legitimately
                                        use. There is deliberately no "show
                                        everything" fallback: a discount can never
                                        belong in a purchase box. The backend
                                        enforces the same. */}
                                    <SettingsSelect
                                        options={vatFieldOptions.filter(
                                            (o) => (finding.allowedFields || []).includes(o.value)
                                        )}
                                        value={row.field}
                                        onChange={(value) => setRow(key, rowIndex, { field: value })}
                                        placeholder="Välj fält…"
                                    />
                                </div>
                                <input
                                    className="form-control"
                                    style={{ maxWidth: 160 }}
                                    type="number"
                                    step="0.01"
                                    placeholder="Belopp"
                                    value={row.amount}
                                    onChange={(e) => setRow(key, rowIndex, { amount: e.target.value })}
                                />
                                {(allocations[key] || []).length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => removeRow(key, rowIndex)}
                                    >
                                        Ta bort
                                    </button>
                                )}
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => addRow(key)}
                            >
                                Lägg till fält
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={savingKey === key || !fiscalYearId || !period}
                                onClick={() => saveAllocation(finding)}
                            >
                                {savingKey === key ? 'Sparar…' : 'Spara fördelning'}
                            </button>
                            <span style={{ fontSize: '0.8125rem', color: 'var(--secondary, #717579)' }}>
                                Att fördela: {finding.balance}
                                {finding.resolved && ' — fördelningen stämmer, men går att ändra.'}
                                {!fiscalYearId && ' — välj ett räkenskapsår först.'}
                                {fiscalYearId && !period && ' — välj en momsperiod först.'}
                            </span>
                        </div>
                    </div>
                )}

            </div>
        );
    };

    const renderGroup = (group, { action }) => {
        const open = isGroupOpen(group);
        return (
            <div
                key={group.kind}
                className={`health-group${open ? ' is-open' : ''}${group.tone === 'error' ? ' health-group--error' : ''}`}
            >
                <button
                    type="button"
                    className="health-group__summary"
                    onClick={() => toggleGroup(group)}
                    aria-expanded={open}
                >
                    <ChevronDown size={14} className="health-group__chevron" aria-hidden="true" />
                    <span className="health-group__label">
                        {KIND_LABELS[group.kind] || group.kind}
                        {!action && KIND_DESCRIPTIONS[group.kind] && (
                            <span style={{ display: 'block', fontWeight: 400, fontSize: '0.8125rem', color: 'var(--secondary, #717579)' }}>
                                {KIND_DESCRIPTIONS[group.kind]}
                            </span>
                        )}
                    </span>
                    <span className="health-group__count">{group.items.length}</span>
                    {group.needsAction > 0 && (
                        <span className="health-group__badge">
                            {group.needsAction} behöver beslut
                        </span>
                    )}
                </button>

                {open && (
                    <div className="health-group__items">
                        {group.items.map(({ finding, index }) => renderFinding(finding, index, { action }))}
                    </div>
                )}
            </div>
        );
    };

    const yearLabel = (fy) => `${fy.year ?? ''} (${String(fy.start_date).slice(0, 10)} – ${String(fy.end_date).slice(0, 10)})`;

    return (
        <SettingsCard
            className="chart-health-section"
            icon={Activity}
            title="Kontoplanshälsa"
            subtitle={`Kontoplanen (${new Intl.NumberFormat('sv-SE').format(health.accountCount || 0)} konton) är kontrollerad mot ${chartLabel(health.chartVersionCode)}.`}
            actions={(
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={load} disabled={loading}>
                    <RefreshCw size={14} className="me-1" /> Uppdatera
                </button>
            )}
        >
            <>
                <style>{`
                    /* Self-contained copies of the finding-item styles this
                       section previously borrowed from SieVerificationModal's
                       inline styles (which only exist while that modal is
                       mounted). */
                    .chart-health-section .warning-item { display: flex; gap: 10px; padding: 10px 12px; border: 1px solid #fde68a; background: #fffbeb; border-radius: 8px; }
                    .chart-health-section .warning-item.warning-error { border-color: #fecaca; background: #fef2f2; }
                    .chart-health-section .warning-icon { flex-shrink: 0; margin-top: 2px; color: #d97706; }
                    .chart-health-section .warning-icon.error-icon { color: #dc2626; }
                    .chart-health-section .warning-type { font-weight: 600; font-size: 0.875rem; }
                    .chart-health-section .warning-message { margin: 2px 0 0; font-size: 0.8125rem; color: #64748b; }
                    .chart-health-section .import-error-box { display: flex; gap: 10px; padding: 10px 12px; border: 1px solid #fecaca; background: #fef2f2; border-radius: 8px; font-size: 0.8425rem; }
                    .chart-health-section .health-finding__facts { display: grid; grid-template-columns: 6.5rem 1fr; gap: 0.125rem 0.75rem; margin: 0; font-size: 0.8125rem; }
                    .chart-health-section .health-finding__facts dt { font-weight: 600; color: #374151; }
                    .chart-health-section .health-finding__facts dd { margin: 0; color: #1e293b; }
                    .chart-health-section .health-block__title { font-weight: 700; font-size: 0.9375rem; color: var(--title, #1e293b); margin: 1rem 0 0.5rem; display: flex; align-items: center; gap: 0.5rem; }
                    .chart-health-section .health-block__title:first-of-type { margin-top: 0; }
                    .chart-health-section .health-block__hint { font-weight: 400; font-size: 0.8125rem; color: var(--secondary, #717579); }
                    /* Decision cards under "Behöver åtgärd": white, amber accent, the account as the head. */
                    .chart-health-section .health-action { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; }
                    .chart-health-section .health-action + .health-action { margin-top: 0.5rem; }
                    .chart-health-section .health-action__head { display: flex; align-items: baseline; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
                    .chart-health-section .health-action__account { display: inline-flex; align-items: baseline; gap: 0.5rem; font-size: 0.9375rem; color: var(--title, #1e293b); }
                    .chart-health-section .health-action__number { font-weight: 700; font-variant-numeric: tabular-nums; }
                    .chart-health-section .health-action__name { font-weight: 600; }
                    .chart-health-section .health-action__amount { font-size: 0.8125rem; font-weight: 600; color: var(--title, #1e293b); white-space: nowrap; font-variant-numeric: tabular-nums; }
                    .chart-health-section .health-action__period { font-weight: 400; color: var(--secondary, #717579); }
                    .chart-health-section .health-action .health-finding__facts { grid-template-columns: 6rem 1fr; gap: 0.25rem 0.75rem; }
                `}</style>

                {/* One context row: the year and the period the findings are
                    about (UX_PLAN.md P3). */}
                <div className="d-flex align-items-end gap-3 flex-wrap mb-3">
                    {fiscalYears.length > 1 && (
                        <div style={{ minWidth: 280 }}>
                            <label htmlFor="chart-health-year" className="modern-label">Räkenskapsår</label>
                            <SettingsSelect
                                id="chart-health-year"
                                options={fiscalYears.map((fy) => ({ value: String(fy.fiscal_year_id), label: yearLabel(fy) }))}
                                value={fiscalYearId ? String(fiscalYearId) : ''}
                                onChange={(value) => onSelectFiscalYear && onSelectFiscalYear(value)}
                                isDisabled={!onSelectFiscalYear}
                            />
                        </div>
                    )}
                    {fiscalYears.length === 1 && (
                        <div className="small text-muted">Räkenskapsår: {yearLabel(fiscalYears[0])}</div>
                    )}
                    {periods.length > 0 && (
                        <div style={{ minWidth: 230 }}>
                            <label htmlFor="chart-health-period" className="modern-label">Momsperiod</label>
                            <SettingsSelect
                                id="chart-health-period"
                                options={periods.map((p) => ({
                                    value: p.vat_reporting_period_id,
                                    label: `${p.start_date} – ${p.end_date}`,
                                }))}
                                value={periodId}
                                onChange={(value) => setPeriodId(value)}
                            />
                        </div>
                    )}
                </div>
                {/* The period scopes the per-period findings only; a missing
                    momsrapportkod is an account-wide question and is checked
                    over the whole year (owner, 2026-09-06). */}
                {periods.length > 0 && (
                    <p className="small text-muted" style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
                        Konton som saknar momsrapportkod visas för hela räkenskapsåret. Momsperioden gäller fördelningar av rabatter och EU-inköp.
                    </p>
                )}

                {/* A scan that could not read a source must never be presented as
                    "nothing found" — that would hide exactly the accounts a user
                    is looking for. */}
                {health.referenceScan && !health.referenceScan.reliable && (
                    <div className="import-error-box" style={{ marginBottom: '1rem' }}>
                        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>
                            Alla källor kunde inte läsas, så listan över oanvända konton är ofullständig.
                            Inga arkiveringsförslag visas förrän det är åtgärdat.
                        </span>
                    </div>
                )}

                {periodError && (
                    <div className="import-error-box" style={{ marginBottom: '1rem' }}>
                        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{periodError} Momsgranskningen visas inte förrän en period är vald.</span>
                    </div>
                )}

                {/* ---- Behöver åtgärd ---- */}
                <div className="health-block__title" data-testid="health-actions-title">
                    <AlertTriangle size={16} style={{ color: actionCount > 0 ? '#b45309' : 'var(--secondary, #717579)' }} />
                    Behöver åtgärd ({actionCount})
                </div>
                {actionCount === 0 && !periodError ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--primary)', marginBottom: '0.5rem' }}>
                        <CheckCircle size={16} />
                        {findings.length === 0
                            ? (period ? 'Inga anmärkningar på kontoplanen.' : 'Inget behöver åtgärdas. Välj en momsperiod för att granska momsen.')
                            : `Inget behöver åtgärdas. ${new Intl.NumberFormat('sv-SE').format(infoCount)} informationsposter nedan.`}
                    </div>
                ) : actionCount > 0 ? (
                    <>
                        <p className="warning-message" style={{ margin: '0 0 0.5rem' }}>
                            {actionCount === 1
                                ? 'Ett konto behöver ett beslut innan momsdeklarationen blir rätt. Det stoppar inte bokföringen.'
                                : `${new Intl.NumberFormat('sv-SE').format(actionCount)} poster behöver ett beslut innan momsdeklarationen blir rätt. De stoppar inte bokföringen.`}
                        </p>
                        {actionGroups.map((group) => renderGroup(group, { action: true }))}
                    </>
                ) : null}

                {/* ---- Information ---- */}
                {infoGroups.length > 0 && (
                    <>
                        <div className="health-block__title" data-testid="health-info-title">
                            <Info size={16} style={{ color: 'var(--secondary, #717579)' }} />
                            Information ({new Intl.NumberFormat('sv-SE').format(infoCount)})
                            <span className="health-block__hint">— inget att göra, sparas som underlag</span>
                        </div>
                        {infoGroups.map((group) => renderGroup(group, { action: false }))}
                    </>
                )}
            </>
        </SettingsCard>
    );
};

export default ChartHealthSection;
export { KIND_LABELS, KIND_DESCRIPTIONS };
